import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, Search, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const EVENTS = {
  account_welcome: "Bienvenida de cuenta",
  account_login_success: "Inicio de sesión exitoso",
  role_consultant_approved: "Consultor aprobado · bienvenida al equipo",
  role_creator_approved: "Creador aprobado · bienvenida al equipo",
  premium_access_approved: "Academia KD9 habilitada",
  "energy.package_purchase.approved": "Recarga Energía KD9 acreditada",
  diagnostic_requested: "Diagnóstico solicitado",
  diagnostic_in_progress: "Diagnóstico en proceso",
  diagnostic_completed: "Diagnóstico finalizado",
  academy_study_ready: "Estudio de Academia listo",
  accompaniment_completed: "Acompañamiento completado",
  talk_service_completed: "Consulta completada",
  support_ticket_resolve: "Soporte resuelto",
  support_ticket_close: "Soporte cerrado",
  marketplace_order_confirmed: "Pedido confirmado",
  marketplace_order_shipped: "Pedido enviado",
  marketplace_order_delivered: "Pedido entregado",
  live_session_started: "Templo Vivo iniciado",
  payment_approved: "Pago aprobado",
  payment_rejected: "Pago rechazado",
  appointment_scheduled: "Cita programada",
  appointment_modified: "Cita modificada",
  appointment_cancelled: "Cita cancelada",
  director_message: "Mensaje del Director",
};

export default function WhatsAppNotifications() {
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [visualCards, setVisualCards] = useState([]);
  const [status, setStatus] = useState("");
  const [eventType, setEventType] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [idLookup, setIdLookup] = useState({ status: "idle", message: "" });
  const [lookupBusy, setLookupBusy] = useState(false);
  const [selected, setSelected] = useState("");
  const [message, setMessage] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const loadUsers = useCallback(async (searchTerm = "") => {
    const params = { limit: 100 };
    if (searchTerm.trim()) params.search = searchTerm.trim();
    const { data } = await api.get("/api/director/whatsapp/users", { params });
    setUsers(data);
  }, []);

  const load = useCallback(async () => {
    const params = {};
    if (status) params.status = status;
    if (eventType) params.event_type = eventType;
    const [h, v, cards] = await Promise.all([
      api.get("/api/director/whatsapp/history", { params }),
      api.get("/api/director/whatsapp/verifications"),
      api.get("/api/director/whatsapp/visual-cards"),
    ]);
    setHistory(h.data);
    setVerifications(v.data);
    setVisualCards(cards.data?.cards || []);
  }, [status, eventType]);

  useEffect(() => { load().catch((e) => setNotice(formatApiError(e))); }, [load]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadUsers(userSearch).catch((e) => setNotice(formatApiError(e)));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [loadUsers, userSearch]);

  const validateKd9Id = async () => {
    const term = userSearch.trim();
    if (!term || lookupBusy) return;
    setLookupBusy(true);
    setIdLookup({ status: "checking", message: "Validando ID KD9…" });
    try {
      const { data } = await api.get("/api/director/whatsapp/users", {
        params: { search: term, limit: 100 },
      });
      setUsers(data);
      const exact = data.find((u) => (u.kd9_id || "").trim().toUpperCase() === term.toUpperCase());
      if (!exact) {
        setSelected("");
        setIdLookup({ status: "invalid", message: "ID inválido · No existe una cuenta KD9 con este identificador." });
        return;
      }
      const whatsappReady = Boolean(
        exact.whatsapp_opt_in &&
        exact.whatsapp_verified_at &&
        (exact.phone_e164 || exact.phone)
      );
      if (whatsappReady) {
        setSelected(exact.id);
        setIdLookup({ status: "valid", message: `ID válido · ${exact.name || exact.email} · WhatsApp autorizado.` });
      } else {
        setSelected("");
        setIdLookup({ status: "warning", message: `ID válido · ${exact.name || exact.email}, pero WhatsApp no está verificado o autorizado.` });
      }
    } catch (e) {
      setSelected("");
      setIdLookup({ status: "invalid", message: formatApiError(e) });
    } finally {
      setLookupBusy(false);
    }
  };

  const send = async () => {
    if (!selected || message.trim().length < 3 || busy) return;
    setBusy(true); setNotice("");
    try {
      const recipient = users.find((u) => u.id === selected);
      const { data } = await api.post("/api/director/whatsapp/messages", {
        user_id: selected,
        phone: recipient?.phone_e164 || recipient?.phone || undefined,
        message: message.trim(),
      });
      if (!data?.ok || data?.status === "failed") {
        setNotice(data?.error ? `No se pudo enviar por WhatsApp: ${data.error}` : "No se pudo enviar el mensaje por WhatsApp.");
        return;
      }
      setMessage("");
      setNotice("Mensaje enviado por WhatsApp y registrado en KD9.");
      await Promise.all([load(), loadUsers(userSearch)]);
    } catch (e) {
      setNotice(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const retryFailed = async () => {
    setBusy(true); setNotice("");
    try {
      const { data } = await api.post("/api/director/whatsapp/retry-failed?limit=50");
      setNotice(`Reintento completado: ${data.sent || 0} enviados · ${data.failed || 0} aún fallidos.`);
      await load();
    } catch (e) { setNotice(formatApiError(e)); } finally { setBusy(false); }
  };

  const force = async (id) => {
    if (!window.confirm("¿Forzar una nueva verificación para este usuario?")) return;
    setBusy(true); setNotice("");
    try {
      await api.post(`/api/director/whatsapp/users/${id}/force-reverification`);
      setNotice("La verificación anterior fue revocada."); await Promise.all([load(), loadUsers(userSearch)]);
    } catch (e) { setNotice(formatApiError(e)); } finally { setBusy(false); }
  };

  const counts = history.reduce((a, x) => ({ ...a, [x.status]: (a[x.status] || 0) + 1 }), {});
  return (
    <div className="space-y-6" data-testid="whatsapp-director-page">
      <div>
        <div className="text-xs font-mont tracking-[.3em] uppercase kd9-gold-text">Director KD9 · Seguridad</div>
        <h1 className="font-cinzel text-2xl md:text-3xl mt-2">WhatsApp y Notificaciones</h1>
        <p className="font-mont text-sm mt-2" style={{ color: "var(--kd9-text-soft)" }}>Verificaciones, mensajes importantes, entregas y errores del canal oficial.</p>
      </div>
      {notice && <div className="kd9-card p-4 text-sm font-mont">{notice}</div>}
      <div className="grid sm:grid-cols-4 gap-3">
        {["sent","delivered","read","failed"].map((s) => <div key={s} className="kd9-card p-4"><div className="text-xs uppercase tracking-widest" style={{color:"var(--kd9-text-soft)"}}>{s}</div><div className="text-2xl font-cinzel mt-1">{counts[s] || 0}</div></div>)}
      </div>
      {visualCards.length > 0 && (
        <div className="kd9-card p-5 space-y-4" data-testid="whatsapp-visual-card-library">
          <div>
            <h2 className="font-cinzel kd9-gold-text">Identidad visual WhatsApp KD9</h2>
            <p className="font-mont text-xs mt-1" style={{ color: "var(--kd9-text-soft)" }}>
              Biblioteca oficial de tarjetas. Las marcadas como automáticas se adjuntan a su evento correspondiente.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            {visualCards.map((card) => (
              <div key={card.key} className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--kd9-border)" }}>
                <img
                  src={card.url || card.path}
                  alt={card.title}
                  className="w-full aspect-[4/5] object-cover bg-black"
                  loading="lazy"
                />
                <div className="p-2.5 bg-black/40">
                  <div className="font-mont text-xs">{card.title}</div>
                  <div className="font-mont text-[10px] mt-1 uppercase tracking-wider" style={{ color: card.automatic_safe ? "var(--kd9-gold)" : "var(--kd9-text-soft)" }}>
                    {card.automatic_safe ? "Automática" : "Biblioteca visual"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="kd9-card p-5 space-y-3">
        <h2 className="font-cinzel kd9-gold-text">Mensaje importante del Director</h2>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--kd9-text-soft)" }} />
          <input
            type="search"
            className="kd9-input w-full pl-10"
            value={userSearch}
            onChange={(e)=>{
              setUserSearch(e.target.value);
              setIdLookup({ status: "idle", message: "" });
              setSelected("");
            }}
            onKeyDown={(e)=>{
              if (e.key === "Enter") {
                e.preventDefault();
                validateKd9Id();
              }
            }}
            placeholder="Escribe el ID KD9…"
            aria-label="Buscar destinatario por ID KD9"
            data-testid="whatsapp-director-user-search"
          />
        </div>
        <button
          type="button"
          className="kd9-btn-primary w-full px-4 py-3 rounded-lg flex items-center justify-center gap-2 font-mont text-xs uppercase tracking-wider"
          disabled={lookupBusy || !userSearch.trim()}
          onClick={validateKd9Id}
          data-testid="whatsapp-director-id-search-button"
          style={{
            opacity: lookupBusy || !userSearch.trim() ? 0.5 : 1,
            cursor: lookupBusy || !userSearch.trim() ? "not-allowed" : "pointer",
          }}
        >
          <Search size={15}/> {lookupBusy ? "Buscando…" : "Buscar ID"}
        </button>
        {idLookup.message && (
          <div
            className="rounded-lg px-3 py-2 text-xs font-mont font-semibold"
            data-testid="whatsapp-director-id-search-result"
            style={{
              color:
                idLookup.status === "valid" ? "#22C55E" :
                idLookup.status === "warning" ? "#FACC15" :
                idLookup.status === "checking" ? "var(--kd9-text-soft)" :
                "#EF4444",
              background:
                idLookup.status === "valid" ? "rgba(34,197,94,0.12)" :
                idLookup.status === "warning" ? "rgba(250,204,21,0.12)" :
                idLookup.status === "checking" ? "rgba(255,255,255,0.04)" :
                "rgba(239,68,68,0.12)",
              border:
                idLookup.status === "valid" ? "1px solid rgba(34,197,94,0.38)" :
                idLookup.status === "warning" ? "1px solid rgba(250,204,21,0.42)" :
                idLookup.status === "checking" ? "1px solid var(--kd9-border)" :
                "1px solid rgba(239,68,68,0.42)",
            }}
          >
            {idLookup.message}
          </div>
        )}
        <select className="kd9-input w-full" value={selected} onChange={(e)=>setSelected(e.target.value)} data-testid="whatsapp-director-user-select">
          <option value="">Selecciona un usuario con WhatsApp autorizado</option>
          {users.filter((u)=>u.whatsapp_opt_in && u.whatsapp_verified_at && (u.phone_e164 || u.phone)).map((u)=><option key={u.id} value={u.id}>{u.name} · {u.kd9_id || "ID KD9 pendiente"} · {u.email} · {u.phone_e164 || u.phone}</option>)}
        </select>
        <textarea className="kd9-input w-full min-h-[100px]" value={message} onChange={(e)=>setMessage(e.target.value)} maxLength={1500} placeholder="Mensaje importante…" />
        <button className="kd9-btn-primary w-full sm:w-auto px-5 py-3 rounded-lg flex items-center justify-center gap-2" disabled={busy || !selected || message.trim().length < 3} onClick={send}><Send size={15}/> {busy ? "Enviando…" : "Enviar por WhatsApp"}</button>
      </div>
      <div className="kd9-card p-5">
        <h2 className="font-cinzel kd9-gold-text mb-4">Cuentas y verificación</h2>
        <div className="space-y-2 max-h-80 overflow-auto">
          {users.map((u)=><div key={u.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b py-3" style={{borderColor:"var(--kd9-border)"}}>
            <div>
              <div className="font-mont text-sm font-semibold">{u.name} · {u.email}</div>
              <div className="text-xs flex flex-wrap items-center gap-2 mt-1">
                <span style={{color:"var(--kd9-text-soft)"}}>{u.kd9_id || "ID KD9 pendiente"}</span>
                <span style={{color:"var(--kd9-text-soft)"}}>· {u.phone_e164 || u.phone || "Sin número"}</span>
                <span
                  className="px-2 py-0.5 rounded-full font-semibold"
                  style={{
                    color: u.whatsapp_verified_at ? "#22C55E" : "#FACC15",
                    background: u.whatsapp_verified_at ? "rgba(34,197,94,0.14)" : "rgba(250,204,21,0.14)",
                    border: u.whatsapp_verified_at ? "1px solid rgba(34,197,94,0.38)" : "1px solid rgba(250,204,21,0.42)",
                  }}
                >
                  {u.whatsapp_verified_at ? "Verificado" : "No verificado"}
                </span>
                <span
                  className="px-2 py-0.5 rounded-full font-semibold"
                  style={{
                    color: u.whatsapp_opt_in ? "#22C55E" : "#EF4444",
                    background: u.whatsapp_opt_in ? "rgba(34,197,94,0.14)" : "rgba(239,68,68,0.14)",
                    border: u.whatsapp_opt_in ? "1px solid rgba(34,197,94,0.38)" : "1px solid rgba(239,68,68,0.42)",
                  }}
                >
                  {u.whatsapp_opt_in ? "Opt-in activo" : "Sin opt-in"}
                </span>
              </div>
            </div>
            <button className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px] flex items-center gap-2" disabled={busy || !u.phone_e164} onClick={()=>force(u.id)}><RefreshCw size={12}/> Forzar verificación</button>
          </div>)}
        </div>
      </div>
      <div className="kd9-card p-5 overflow-x-auto">
        <h2 className="font-cinzel kd9-gold-text mb-4">Historial de verificaciones OTP</h2>
        <table className="w-full text-left text-sm font-mont"><thead><tr style={{color:"var(--kd9-text-soft)"}}><th className="p-2">Fecha</th><th className="p-2">Destino</th><th className="p-2">Propósito</th><th className="p-2">Entrega</th><th className="p-2">Intentos</th><th className="p-2">Error</th></tr></thead>
          <tbody>{verifications.map((v)=><tr key={v.id} className="border-t" style={{borderColor:"var(--kd9-border)"}}><td className="p-2 whitespace-nowrap">{new Date(v.created_at).toLocaleString()}</td><td className="p-2">{v.phone}</td><td className="p-2">{v.purpose}</td><td className="p-2">{v.delivery_status}</td><td className="p-2">{v.attempts || 0}</td><td className="p-2 text-xs">{v.delivery_error || "—"}</td></tr>)}</tbody>
        </table>
      </div>
      <div className="kd9-card p-5 overflow-x-auto">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select className="kd9-input" value={status} onChange={(e)=>setStatus(e.target.value)}><option value="">Todos los estados</option>{["pending","sent","delivered","read","failed"].map(s=><option key={s}>{s}</option>)}</select>
          <select className="kd9-input" value={eventType} onChange={(e)=>setEventType(e.target.value)}><option value="">Todos los eventos</option>{Object.entries(EVENTS).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select>
          <button className="kd9-btn-outline px-3 rounded-lg min-h-10" onClick={load} aria-label="Actualizar historial"><RefreshCw size={14}/></button>
          <button className="kd9-btn-outline px-4 min-h-10 rounded-lg text-[10px] font-mont uppercase tracking-wider" disabled={busy || !counts.failed} onClick={retryFailed}>
            Reintentar fallidos ({counts.failed || 0})
          </button>
        </div>
        <table className="w-full text-left text-sm font-mont"><thead><tr style={{color:"var(--kd9-text-soft)"}}><th className="p-2">Fecha</th><th className="p-2">Evento</th><th className="p-2">Estado</th><th className="p-2">Destino</th><th className="p-2">Error</th></tr></thead>
          <tbody>{history.map((h)=><tr key={h.id} className="border-t" style={{borderColor:"var(--kd9-border)"}}><td className="p-2 whitespace-nowrap">{new Date(h.created_at).toLocaleString()}</td><td className="p-2">{EVENTS[h.event_type] || h.event_type}</td><td className="p-2"><span className="inline-flex items-center gap-1">{h.status === "failed" ? <AlertTriangle size={13} color="#FCA5A5"/> : <CheckCircle2 size={13} color="#86EFAC"/>}{h.status}</span></td><td className="p-2">{h.phone}</td><td className="p-2 text-xs max-w-xs break-words">{h.error || "—"}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}
